/**
 * Forced password change.
 *
 * The form is deliberately the only thing between a seeded operator and the rest
 * of the application, so it has one job: make the correct password easy to enter
 * and every other outcome impossible to ignore.
 *
 * On success it does *not* navigate anywhere itself. The session is gone by then —
 * `changePassword` revokes every refresh token — so the app re-renders with no
 * session and `RequireSession` bounces to `/login`. Letting the redirect happen
 * through one code path avoids this page and the router disagreeing about when
 * the user is signed out.
 */

import { useState, type FormEvent } from 'react';

import { changePasswordSchema } from '@retailpro/shared';

import { useUi } from '../i18n/ui-context';
import { useSession } from '../session/session-context';
import { ApiError, type Session } from '../lib/api-client';

type FieldErrors = Partial<Record<'currentPassword' | 'newPassword' | 'confirmPassword', string>>;

export function ChangePasswordPage() {
  const { session } = useSession();

  // Split rather than an early `return null`: TypeScript does not carry a
  // narrowing of `session` into a function *declaration* defined later in the
  // body, so the submit handler would keep seeing `Session | null`. Passing the
  // session to a child makes the guarantee structural instead of a claim the
  // compiler has to be argued into.
  if (!session) return null;

  return <ChangePasswordForm session={session} />;
}

function ChangePasswordForm({ session }: { session: Session }) {
const { api } = useSession();
  const { t } = useUi();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Offered even when the flag is already clear: a user who suspects a shoulder-surf
  // or a compromised terminal should be able to rotate on demand, not only when
  // an administrator forces it.
  const forced = session.user.mustChangePassword;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    // The same schema the server runs, so an obviously weak password is caught
    // before a round trip and before argon2 is asked to hash it.
    const parsed = changePasswordSchema.safeParse({ currentPassword, newPassword, confirmPassword });
    if (!parsed.success) {
      setFieldErrors(collect(parsed.error.issues));
      return;
    }

    setSubmitting(true);
    try {
      await api.changePassword(
        {
          currentPassword: parsed.data.currentPassword,
          newPassword: parsed.data.newPassword,
          confirmPassword: parsed.data.confirmPassword,
        },
        session.accessToken,
      );
    } catch (error) {
      // A wrong current password is the expected failure, not an exception: the
      // server counts it toward the throttle, so the message has to stay calm and
      // must not tell an attacker whether anything else was wrong.
      if (error instanceof ApiError) {
        // `errors` is only present on VALIDATION_FAILED, and the server sends one
        // entry per field so the message lands next to the input that caused it.
        // Everything else — a wrong current password, a throttled address, a 500 —
        // is a form-level message, because there is no field it belongs to.
        const fields = error.problem?.errors;
        const known = pickKnownFields(fields);
        if (known) {
          setFieldErrors(known);
        } else {
          setFormError(error.message);
        }
      } else {
        setFormError(t('password.genericError'));
      }
      setSubmitting(false);
    }
  }

  return (
    <main>
      <h1>{forced ? t('password.title') : t('password.titleVoluntary')}</h1>

{forced ? <p role="status">{t('password.forced')}</p> : null}

      <form onSubmit={onSubmit} noValidate>
        <label htmlFor="currentPassword">{t('password.current')}</label>
        <input
          id="currentPassword"
          name="currentPassword"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          aria-invalid={fieldErrors.currentPassword ? true : undefined}
          aria-describedby={fieldErrors.currentPassword ? 'currentPassword-error' : undefined}
        />
        {fieldErrors.currentPassword ? (
          <span id="currentPassword-error" role="alert">
            {fieldErrors.currentPassword}
          </span>
        ) : null}

        <label htmlFor="newPassword">{t('password.new')}</label>
        <input
          id="newPassword"
          name="newPassword"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          aria-invalid={fieldErrors.newPassword ? true : undefined}
          aria-describedby="newPassword-hint"
        />
<span id="newPassword-hint">{t('password.hint')}</span>
        {fieldErrors.newPassword ? (
          <span role="alert">{fieldErrors.newPassword}</span>
        ) : null}

        <label htmlFor="confirmPassword">{t('password.confirm')}</label>
        <input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          aria-invalid={fieldErrors.confirmPassword ? true : undefined}
          aria-describedby={fieldErrors.confirmPassword ? 'confirmPassword-error' : undefined}
        />
        {fieldErrors.confirmPassword ? (
          <span id="confirmPassword-error" role="alert">
            {fieldErrors.confirmPassword}
          </span>
        ) : null}

        {formError ? <p role="alert">{formError}</p> : null}

        <button type="submit" disabled={submitting}>
          {submitting ? t('password.submitting') : t('password.submit')}
        </button>
      </form>
    </main>
  );
}

/** Map zod issues onto form fields, keeping the first message per field. */
function collect(issues: { path: PropertyKey[]; message: string }[]): FieldErrors {
  const out: FieldErrors = {};
  for (const issue of issues) {
    const key = String(issue.path[0]) as keyof FieldErrors;
    if (!(key in out)) out[key] = issue.message;
  }
  return out;
}

/**
 * Take the server's per-field errors, but only for fields this form has.
 *
 * Returns `null` when nothing maps, so the caller shows a form-level message
 * instead of an empty error list that would look like the form silently ignored
 * the response.
 */
function pickKnownFields(errors: Record<string, string[]> | undefined): FieldErrors | null {
  if (!errors) return null;
  const out: FieldErrors = {};
  for (const key of ['currentPassword', 'newPassword', 'confirmPassword'] as const) {
    const first = errors[key]?.[0];
    if (first) out[key] = first;
  }
  return Object.keys(out).length > 0 ? out : null;
}